import { useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';

import { useCreateSubCategory } from '../../../categories/hooks/create-sub-category.hook';
import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { Category } from '../../../categories/types/category.type';
import {
  CATEGORY_NAME_MAX_LENGTH,
  CATEGORY_NAME_MIN_LENGTH,
} from '../../../categories/constants/category-name-length.constants';

type Props = {
  onSubCategoryCreated: () => void;
};

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  checkbox: {
    '&.Mui-checked': {
      color: theme.palette.admin.primary.main,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function CreateSubCategoryForm({ onSubCategoryCreated }: Props) {
  const classes = useStyles();
  const { createSubCategory, isSubmitting } = useCreateSubCategory();
  const { categories } = useAdminGetCategories();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const isFormDirty = name !== '' || description !== '' || !isActive || selectedCategory !== null;
  const isValid =
    trimmedName.length >= CATEGORY_NAME_MIN_LENGTH && trimmedDescription !== '' && selectedCategory !== null;
  const isSubmitDisabled = isSubmitting || !isValid;

  const handleReset = () => {
    setName('');
    setDescription('');
    setIsActive(true);
    setSelectedCategory(null);
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled || !selectedCategory) return;

    const result = await createSubCategory({
      name: trimmedName,
      description: trimmedDescription,
      isActive,
      category: selectedCategory.id,
    });

    if (result) {
      handleReset();
      onSubCategoryCreated();
    }
  };

  return (
    <div className={classes.form}>
      <TextField
        id="create-sub-category-name"
        label="Nom"
        type="text"
        inputProps={{ maxLength: CATEGORY_NAME_MAX_LENGTH }}
        value={name}
        size="small"
        variant="outlined"
        fullWidth
        required
        onChange={(event) => setName(event.target.value)}
      />
      <Autocomplete
        size="small"
        value={selectedCategory}
        options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
        groupBy={(option) => option.name[0].toUpperCase()}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, selected) => option.id === selected.id}
        onChange={(event, selectedOption: Category | null) => setSelectedCategory(selectedOption)}
        renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" required />}
      />
      <TextField
        id="create-sub-category-description"
        label="Description"
        type="text"
        value={description}
        size="small"
        variant="outlined"
        fullWidth
        required
        multiline
        minRows={3}
        onChange={(event) => setDescription(event.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="default"
            className={classes.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        }
        label="Actif"
      />
      <div className={classes.actions}>
        <Button size="small" variant="outlined" onClick={handleReset} disabled={!isFormDirty || isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Créer
        </Button>
      </div>
    </div>
  );
}
